import { useEffect, useState } from 'react';
import type { Fixture, League, Round, RoundStatus } from '@h2h/shared';
import {
  FOCUSED_LEAGUE_ID,
  LEAGUE_IDENTITIES,
  leagueSeasonStartAt,
  leagueWaitingNote,
  type LeagueIdentity
} from '../../data/leagues';
import { countdownParts } from '../../lib/format';

export const FOOTBALL_IDENTITIES = LEAGUE_IDENTITIES.filter((l) => l.leagueId !== FOCUSED_LEAGUE_ID);

export function isRoundInPlay(status: RoundStatus | undefined): boolean {
  return status === 'OPEN' || status === 'LOCKING' || status === 'SETTLING';
}

export function formatSeasonStart(leagueId: string): string {
  const start = leagueSeasonStartAt(leagueId);
  if (start == null) return leagueWaitingNote(leagueId);
  const label = new Date(start).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
  return `Starts ${label}`;
}

export function formatWaitingSubtitle(identity: LeagueIdentity): string {
  const start = formatSeasonStart(identity.leagueId);
  return identity.country ? `${identity.country} · ${start}` : start;
}

export function liveFixture(fixtures: Fixture[] | undefined): Fixture | undefined {
  return fixtures?.find((f) => f.status === 'LIVE');
}

export function fixtureScoreline(f: Fixture): string {
  const home = f.homeCode ?? f.homeTeam.split(/\s+/)[0] ?? f.homeTeam;
  const away = f.awayCode ?? f.awayTeam.split(/\s+/)[0] ?? f.awayTeam;
  const hg = f.liveHomeGoals ?? f.result?.homeGoals ?? 0;
  const ag = f.liveAwayGoals ?? f.result?.awayGoals ?? 0;
  return `${home} ${hg}–${ag} ${away}`;
}

export function picksOpenCount(round: Round | null | undefined, picked: number): number {
  if (!round) return 0;
  return Math.max(0, round.picksRequired - picked);
}

export function useCountdown(target: number | null | undefined) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (target == null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [target]);
  return target != null ? countdownParts(target, now) : null;
}

export function nextScheduledKickoff(rounds: Round[] | undefined, current: Round | null | undefined): number | null {
  if (current?.status === 'SCHEDULED') return current.firstKickoffAt;
  const upcoming = (rounds ?? [])
    .filter((r) => r.status === 'SCHEDULED' && r.firstKickoffAt > Date.now())
    .sort((a, b) => a.firstKickoffAt - b.firstKickoffAt);
  return upcoming[0]?.firstKickoffAt ?? null;
}

export function cyclingAccordionSubtitle(upcoming: League[], liveCount: number): string {
  if (upcoming.length === 0) return 'Finished races are hidden';
  if (liveCount > 0) {
    return `${upcoming.length} upcoming · ${liveCount} in play`;
  }
  const next = upcoming.find((l) => l.currentRoundId)?.name ?? upcoming[0]?.name;
  return `${upcoming.length} upcoming${next ? ` · ${next} next` : ''}`;
}
